import React, { useState, useRef, useEffect } from 'react';
import {
  InputAdornment,
  MenuItem,
  SelectChangeEvent,
  Fade,
} from '@mui/material';
import { TooltipTopArrow } from 'icons';
import * as Styled from './MBSearchFormV2.styles';
import { MBSearchFormV2Props } from './MBSearchFormV2.types';
import { KeyCodesEnum } from '../../../typings/enums';

const langList = [
  { text: 'English', value: 'en' },
  { text: 'French', value: 'fr' },
  { text: 'German', value: 'de' },
  { text: 'Italian', value: 'it' },
  { text: 'Spanish', value: 'es' },
];

const MBSearchFormV2 = React.forwardRef<HTMLInputElement, MBSearchFormV2Props>(
  (
    {
      onImageSearchClick,
      value,
      onInputFocus,
      onInputChange,
      onSubmit,
      onReset,
      onClick,
      placeholder,
      selectedLang,
      newLang,
      withLangSelect,
      onLangChange,
      withImageSearch = false,
      imageSearchTooltip = 'Search by Image',
      newTooltipLabel = 'New',
      isVisualSearchNewFeature,
      isLeftRightArrowsActive = true,
    },
    externalRef,
  ) => {
    const [currentLang, setCurrentLang] = useState(
      selectedLang || langList[0]?.value,
    );
    const [isTooltipHovered, setIsTooltipHovered] = useState(false);
    const searchInput = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
      if (externalRef && typeof externalRef === 'object') {
        externalRef.current = searchInput.current;
      }
    }, [externalRef]);

    useEffect(() => {
      if (newLang != null) {
        setCurrentLang(newLang);
      }
    }, [newLang]);

    const handleLangChange = (event: SelectChangeEvent<any>) => {
      const value = event.target.value;
      setCurrentLang(value);
      onLangChange?.(value);
    };

    const handleSearchIconClick = () => {
      onSubmit(value);
    };

    const handleResetClick = () => {
      onReset?.();
    };

    const handleClick = () => {
      onClick?.();
    };

    const handleTextFieldChange = (
      event: React.ChangeEvent<HTMLInputElement>,
    ) => {
      const newValue = event.target.value;
      onInputChange(newValue);
    };
    const handleInputFocus = (e: React.FocusEvent) => {
      e.preventDefault();
      onInputFocus?.();
    };
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      // prevent Y arrows event
      if ([38, 40].includes(e.keyCode)) {
        e.preventDefault();
      }
      // prevent X arrows event on condition
      if (!isLeftRightArrowsActive && [37, 39].includes(e.keyCode)) {
        e.preventDefault();
      }
      if (
        e.code === KeyCodesEnum.ENTER ||
        e.code === KeyCodesEnum.NUMPAD_ENTER
      ) {
        onSubmit(value);
        if (searchInput?.current != null) {
          searchInput.current.blur();
        }
      }
    };

    const handleImageSearch = () => {
      onImageSearchClick?.();
    };

    const handleMouseEnter = () => {
      setIsTooltipHovered(true);
    };
    const handleMouseLeave = () => {
      setIsTooltipHovered(false);
    };

    const startAdornment = (
      <InputAdornment position="end">
        <Styled.SearchIcon
          name="search"
          dense
          onClick={handleSearchIconClick}
        />
      </InputAdornment>
    );

    const endAdornment = value ? (
      <InputAdornment position="end">
        <Styled.CloseIcon
          id="main_search_form_clear_button"
          onClick={handleResetClick}
        />
      </InputAdornment>
    ) : null;

    const tooltipTitle = isVisualSearchNewFeature
      ? isTooltipHovered
        ? imageSearchTooltip
        : newTooltipLabel
      : imageSearchTooltip;
    const isToolTipOpen =
      isVisualSearchNewFeature == undefined
        ? isTooltipHovered
        : isVisualSearchNewFeature || isTooltipHovered;

    return (
      <Styled.FormWrapper>
        <Styled.Field
          inputRef={searchInput}
          placeholder={placeholder}
          onChange={handleTextFieldChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          onClick={handleClick}
          value={value}
          autoComplete="off"
          // @ts-expect-error // TODO: fix
          variant="outlined"
          InputProps={{
            startAdornment,
            endAdornment: endAdornment,
            inputProps: {
              'aria-label': 'Search through site content',
            },
          }}
        />
        <Styled.TooltipContainer>
          {withImageSearch && (
            <Styled.Tooltip
              arrow
              open={isToolTipOpen}
              title={tooltipTitle}
              PopperProps={{
                disablePortal: true,
              }}
              slots={{
                arrow: TooltipTopArrow,
              }}
              TransitionComponent={Fade}
              // @ts-expect-error // TODO: fix types
              isNewFeature={isVisualSearchNewFeature && !isTooltipHovered}
            >
              <Styled.ImageSearchButton
                id="imageSearchTriggerButton"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                iconName="camera"
                dense
                onClick={handleImageSearch}
              />
            </Styled.Tooltip>
          )}
        </Styled.TooltipContainer>
        {withLangSelect && (
          <Styled.Select
            unstyled
            value={currentLang}
            onChange={handleLangChange}
          >
            {langList.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.text}
              </MenuItem>
            ))}
          </Styled.Select>
        )}
      </Styled.FormWrapper>
    );
  },
);

MBSearchFormV2.displayName = 'MBSearchFormV2';

export default MBSearchFormV2;
